// Pure builders for the Radar signal groups. Each takes the snapshot
// data and returns the group's items in display order; RadarPage only renders.
import { daysUntil } from "../../shared/dates";
import { keyDateRadarItems } from "../keydates/keyDates";

// The groups that list tasks by their deadline.
const DEADLINE_GROUPS = ["overdue", "duetoday", "duesoon"];

// The ids of tasks Radar shows under Overdue, Tasks Due Today or Tasks Due Soon (`byDeadline`, their lists by group
// id), leaving out a group the user turned off (`groupsOff`, the stored radarGroupsOff). A Key Date's task in this set
// isn't repeated under Key Dates.
export const shownByDeadlineGroups = (byDeadline, groupsOff = []) => {
  const off = new Set(groupsOff || []);
  return new Set(DEADLINE_GROUPS.filter(id => !off.has(id)).flatMap(id => byDeadline[id] || []).map(t => t.id));
};

// How many tasks Radar lists today: the deadline groups and the Key Dates group's tasks, each counted once, leaving
// out a group the user turned off. Goals aren't tasks, so the goal groups don't count (Craig, 23 Oct 2026, asked for
// "71 tasks on my radar today"; the old count, 71 "things", included 52 goals). keyDateItems already leaves out a
// task a deadline group shows (keyDateRadarItems), so nothing counts twice.
export const radarTaskCount = ({ keyDateItems, ...byDeadline }, groupsOff = []) =>
  shownByDeadlineGroups(byDeadline, groupsOff).size + ((groupsOff || []).includes("keydates") ? 0 : keyDateItems.length);

// How many items a Radar group shows before "Show more"; the Goals page's Top shows as many.
export const RADAR_GROUP_CAP = 5;

// Radar's groups in their starting order, which the user can change by dragging; Radar settings lists them in the
// order the page shows. Craig, 24 Oct 2026: "Overdue Tasks, Key Dates Approaching, Tasks Due Soon, Urgent & Important
// Goals, Most Important Goals". (Until then the goals came first: importance before urgency, 12 Oct 2026.) Craig added
// Tasks Due Today, Habits, Dreams and Fears on 25 Oct 2026; Due Today follows Overdue, and the other three, which
// aren't about today, follow the goals. What Matters Most shows only while Vision does, so it goes last.
export const RADAR_GROUPS = [
  { id: "overdue",         label: "Overdue Tasks",                  desc: "Tasks past their deadline" },
  { id: "duetoday",        label: "Tasks Due Today",                desc: "Tasks with today's deadline" },
  { id: "keydates",        label: "Key Dates Coming Up",            desc: "Key dates you're getting ready for" },
  { id: "duesoon",         label: "Tasks Due Soon",                 desc: "Tasks due in the next few days" },
  { id: "urgentimportant", label: "Urgent & Important Goals",       desc: "Goals you ranked high for both urgency and importance" },
  { id: "mostimportant",   label: "Most Important Goals",           desc: "The goals you ranked most important" },
  { id: "habits",          label: "Habits",                         desc: "The habits you're building or cutting back" },
  { id: "dreams",          label: "Dreams",                         desc: "The things you want, in your Dreamboard's order" },
  { id: "fears",           label: "Fears",                          desc: "What putting things off could cost you" },
  { id: "whatmatters",     label: "What Matters Most (Your Vision)", desc: "Vision behind your top goals" },
];

// The order to show the groups in: the user's saved order, keeping only groups that still exist. A group the saved
// order doesn't have (one added since) goes after the group it follows in the starting order, so Due Today lands
// under Overdue wherever the user moved it.
export const radarGroupOrder = (saved) => {
  const ids = RADAR_GROUPS.map(g => g.id);
  if (!Array.isArray(saved)) return ids;
  const order = saved.filter(id => ids.includes(id));
  ids.forEach((id, i) => {
    if (order.includes(id)) return;
    order.splice(i === 0 ? 0 : order.indexOf(ids[i - 1]) + 1, 0, id);
  });
  return order;
};

// Number of top urgent+important goals whose visions surface in What Matters Most.
export const TOP_GOALS_FOR_VISION = 5;

// Deadline ascending, undated last, then newest first.
export const radarTaskSort = (a, b) => {
  if (a.deadline && b.deadline) { const d = a.deadline.localeCompare(b.deadline); if (d !== 0) return d; }
  if (a.deadline && !b.deadline) return -1;
  if (!a.deadline && b.deadline) return 1;
  return (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0);
};

const byDeadline = (a, b) => a.deadline.localeCompare(b.deadline);

export const overdueTasks = (activeTasks) =>
  activeTasks.filter(t => t.deadline && daysUntil(t.deadline) < 0).sort(byDeadline);

// Due `firstDay` days from now (0, today, unless given) through `windowDays` days from now, inclusive. Radar's
// Tasks Due Soon starts tomorrow, since Tasks Due Today lists today's.
export const dueSoonTasks = (activeTasks, windowDays, firstDay = 0) =>
  activeTasks.filter(t => {
    const d = t.deadline ? daysUntil(t.deadline) : null;
    return d !== null && d >= firstDay && d <= windowDays;
  }).sort(byDeadline);

export const dueTodayTasks = (activeTasks) => dueSoonTasks(activeTasks, 0);

// A goal's rank is its place in the order (1, 2, 3…), not the stored number, which has gaps once a goal
// is finished or deleted. The Goals page and the add-task sheet number goals the same way.
const placesBy = (goals, field) => new Map(
  goals.filter(g => g[field] != null).sort((a, b) => a[field] - b[field]).map((g, n) => [g.id, n + 1]));

// Goals ranked on both lists, by the sum of their two ranks (lower = higher); a tie goes to the more important.
// Radar's Urgent & Important Goals and the Goals page's Top both list this.
export const urgentImportantGoals = (activeGoals) => {
  const u = placesBy(activeGoals, "goalUrgencyOrder"), i = placesBy(activeGoals, "goalImportanceOrder");
  return activeGoals
    .filter(g => u.has(g.id) && i.has(g.id))
    .map(g => ({ goal: g, score: u.get(g.id) + i.get(g.id), u: u.get(g.id), i: i.get(g.id) }))
    .sort((a, b) => a.score - b.score || a.i - b.i);
};

export const mostImportantGoals = (activeGoals) => {
  const i = placesBy(activeGoals, "goalImportanceOrder");
  return activeGoals.filter(g => i.has(g.id)).map(g => ({ goal: g, i: i.get(g.id) })).sort((a, b) => a.i - b.i);
};

// Visions linked to any of the top urgent+important goals, best-ranked first,
// each with its linked top goals in rank order.
export const whatMattersMost = (visions, activeGoals) => {
  const ranked = urgentImportantGoals(activeGoals);
  const top = ranked.slice(0, TOP_GOALS_FOR_VISION);
  const rankById = new Map(top.map(r => [r.goal.id, r]));
  return (visions || [])
    .map(v => {
      const linked = (v.goalIds || [])
        .filter(gid => rankById.has(gid))
        .map(gid => rankById.get(gid))
        .sort((a, b) => a.score - b.score);
      if (linked.length === 0) return null;
      return { vision: v, bestScore: linked[0].score, topGoals: linked.map(r => r.goal) };
    })
    .filter(Boolean)
    .sort((a, b) => a.bestScore - b.bestScore);
};

// How far ahead Tasks Due Soon looks, in days: the user's choice in Radar settings, or a week.
export const dueSoonWindow = (settings) => settings?.radarDueSoonWindow ?? 7;

// "N tasks on my radar today" from the snapshot: what Radar's header says and the sidebar's count beside Radar, so the
// two always agree. `settings` gives the groups turned off and how far ahead Tasks Due Soon looks.
export const radarTodayCount = (tasks, keyDates, settings) => {
  const active = (tasks || []).filter(t => !t.done);
  const byDeadline = { overdue: overdueTasks(active), duetoday: dueTodayTasks(active), duesoon: dueSoonTasks(active, dueSoonWindow(settings), 1) };
  const groupsOff = settings?.radarGroupsOff;
  const keyDateItems = keyDateRadarItems(keyDates || [], active, undefined, shownByDeadlineGroups(byDeadline, groupsOff));
  return radarTaskCount({ ...byDeadline, keyDateItems }, groupsOff);
};
